(() => {
  const ELEMENT_NAME = 'ironside-benefits-marquee';

  if (customElements.get(ELEMENT_NAME)) return;

  class IronsideBenefitsMarquee extends HTMLElement {
    constructor() {
      super();

      this.viewport = null;
      this.track = null;
      this.source = null;
      this.reducedMotionQuery = null;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.animationFrame = 0;
      this.rebuildTimer = 0;
      this.position = 0;
      this.cycleWidth = 0;
      this.lastTimestamp = 0;
      this.lastObservedWidth = 0;
      this.isVisible = true;
      this.isPaused = false;
      this.isReady = false;
      this.mode = 'idle';
      this.speed = 33;

      this.handlePointerEnter = this.handlePointerEnter.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
      this.tick = this.tick.bind(this);
    }

    connectedCallback() {
      if (this.isReady) return;

      this.viewport = this.querySelector('[data-isbm-viewport]');
      this.track = this.querySelector('[data-isbm-track]');
      this.source = this.querySelector('[data-isbm-source]');

      if (!this.viewport || !this.track || !this.source) return;

      this.speed = this.detectWindows()
        ? Number(this.dataset.speedWindows) || 60
        : Number(this.dataset.speedNormal) || 33;

      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.addEventListener('pointerenter', this.handlePointerEnter, { passive: true });
      this.addEventListener('pointerleave', this.handlePointerLeave, { passive: true });
      this.addMediaListener(this.reducedMotionQuery, this.handleMediaChange);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', this.handleResize, { passive: true });
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
          root: null,
          rootMargin: '240px 0px',
          threshold: 0
        });
        this.intersectionObserver.observe(this);
      }

      this.isReady = true;
      this.syncMode({ preservePosition: false, forceRebuild: true });

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) {
            this.scheduleRebuild(true);
          }
        });
      }
    }

    disconnectedCallback() {
      this.stop();
      window.clearTimeout(this.rebuildTimer);
      this.removeClones();
      this.clearMeasuredWidths();

      this.removeEventListener('pointerenter', this.handlePointerEnter);
      this.removeEventListener('pointerleave', this.handlePointerLeave);
      this.removeMediaListener(this.reducedMotionQuery, this.handleMediaChange);

      if (this.resizeObserver) this.resizeObserver.disconnect();
      else window.removeEventListener('resize', this.handleResize);

      if (this.intersectionObserver) this.intersectionObserver.disconnect();

      this.classList.remove('is-resizing');
      this.isReady = false;
    }

    addMediaListener(query, handler) {
      if (!query) return;
      if (query.addEventListener) query.addEventListener('change', handler);
      else query.addListener(handler);
    }

    removeMediaListener(query, handler) {
      if (!query) return;
      if (query.removeEventListener) query.removeEventListener('change', handler);
      else query.removeListener(handler);
    }

    handlePointerEnter() {
      this.isPaused = true;
    }

    handlePointerLeave() {
      this.isPaused = false;
      this.lastTimestamp = 0;
    }

    handleMediaChange() {
      this.scheduleRebuild(true);
    }

    handleResize(entries) {
      const measuredWidth = entries?.[0]?.contentRect?.width
        || this.getBoundingClientRect().width
        || window.innerWidth;

      if (Math.abs(measuredWidth - this.lastObservedWidth) < 2) return;

      this.lastObservedWidth = measuredWidth;
      this.classList.add('is-resizing');
      this.stop();
      this.forceClippedViewport();
      this.resetNativeScroll();
      this.scheduleRebuild(true);
    }

    handleIntersection(entries) {
      const entry = entries.find((item) => item.target === this);
      if (!entry) return;

      this.isVisible = entry.isIntersecting || entry.intersectionRatio > 0;

      if (!this.isVisible) {
        this.stop();
        return;
      }

      if (this.mode === 'marquee') this.start();
      else this.syncMode({ preservePosition: true, forceRebuild: false });
    }

    scheduleRebuild(preservePosition) {
      window.clearTimeout(this.rebuildTimer);
      this.rebuildTimer = window.setTimeout(() => {
        if (!this.isConnected) return;
        this.syncMode({ preservePosition, forceRebuild: true });
        this.classList.remove('is-resizing');
      }, 140);
    }

    getVisibleCount() {
      return Math.max(1, Number(this.dataset.visibleCount) || this.source.children.length || 1);
    }

    getStaticItemMinWidth() {
      return Number(this.dataset.staticItemMinWidth) || 280;
    }

    shouldScroll() {
      const count = this.getVisibleCount();
      if (count <= 1) return false;

      const width = this.viewport.clientWidth
        || this.getBoundingClientRect().width
        || window.innerWidth;

      return (width / count) < this.getStaticItemMinWidth();
    }

    getReducedMotionBehavior() {
      return this.dataset.reducedMotionBehavior === 'manual' ? 'manual' : 'continue';
    }

    shouldUseManualScroll() {
      return Boolean(
        this.reducedMotionQuery
        && this.reducedMotionQuery.matches
        && this.getReducedMotionBehavior() === 'manual'
      );
    }

    syncMode({ preservePosition = false, forceRebuild = false } = {}) {
      if (!this.viewport || !this.track || !this.source) return;

      this.classList.remove('is-initializing');
      this.classList.toggle(
        'has-reduced-motion-preference',
        Boolean(this.reducedMotionQuery && this.reducedMotionQuery.matches)
      );

      if (!this.shouldScroll()) {
        if (this.mode !== 'static') this.setStaticMode();
        return;
      }

      if (this.shouldUseManualScroll()) {
        if (this.mode !== 'manual') this.setManualScrollMode();
        return;
      }

      if (this.mode !== 'marquee') {
        this.setAutomaticScrollMode(preservePosition);
        return;
      }

      if (forceRebuild) {
        this.build(preservePosition);
      }

      if (this.isVisible) this.start();
    }

    clearModeClasses() {
      this.classList.remove(
        'is-static-mode',
        'is-marquee-mode',
        'is-auto-scrolling',
        'is-reduced-motion'
      );
    }

    clearMeasuredWidths() {
      if (!this.source) return;
      this.source.style.removeProperty('width');
      this.source.style.removeProperty('min-width');
      this.source.style.removeProperty('flex-basis');
    }

    resetNativeScroll() {
      if (!this.viewport) return;
      this.viewport.scrollLeft = 0;
      this.viewport.scrollTop = 0;
    }

    forceClippedViewport() {
      if (!this.viewport) return;
      const clipped = window.CSS && CSS.supports && CSS.supports('overflow', 'clip');
      this.viewport.style.overflowX = clipped ? 'clip' : 'hidden';
      this.viewport.style.overflowY = clipped ? 'clip' : 'hidden';
    }

    allowManualViewport() {
      if (!this.viewport) return;
      this.viewport.style.overflowX = 'auto';
      this.viewport.style.overflowY = 'hidden';
    }

    setStaticMode() {
      this.stop();
      this.removeClones();
      this.clearMeasuredWidths();
      this.clearModeClasses();
      this.classList.add('is-static-mode');
      this.mode = 'static';
      this.position = 0;
      this.cycleWidth = 0;
      this.lastTimestamp = 0;
      this.track.style.left = '0px';
      this.forceClippedViewport();
      this.resetNativeScroll();
    }

    setManualScrollMode() {
      this.stop();
      this.removeClones();
      this.clearMeasuredWidths();
      this.clearModeClasses();
      this.classList.add('is-marquee-mode', 'is-reduced-motion');
      this.mode = 'manual';
      this.position = 0;
      this.cycleWidth = 0;
      this.lastTimestamp = 0;
      this.track.style.left = '0px';
      this.allowManualViewport();
      this.resetNativeScroll();
    }

    setAutomaticScrollMode(preservePosition) {
      this.stop();
      this.forceClippedViewport();
      this.resetNativeScroll();
      this.clearModeClasses();
      this.classList.add('is-marquee-mode', 'is-auto-scrolling');
      this.mode = 'marquee';
      this.build(preservePosition);
      if (this.isVisible) this.start();
    }

    build(preservePosition) {
      const oldWidth = this.cycleWidth;
      const oldRatio = oldWidth > 0 ? this.position / oldWidth : 0;

      this.stop();
      this.removeClones();
      this.clearMeasuredWidths();
      this.track.style.left = '0px';

      const measuredWidth = this.source.getBoundingClientRect().width
        || this.source.scrollWidth
        || 0;

      if (!measuredWidth) {
        this.position = 0;
        this.cycleWidth = 0;
        return;
      }

      const exactWidth = Number(measuredWidth.toFixed(3));
      this.applyExactGroupWidth(this.source, exactWidth);
      this.cycleWidth = exactWidth;

      const viewportWidth = this.viewport.clientWidth || 0;
      const totalGroupsNeeded = Math.max(
        2,
        Math.ceil((viewportWidth + exactWidth) / exactWidth)
      );
      const cloneCount = totalGroupsNeeded - 1;
      const fragment = document.createDocumentFragment();

      for (let index = 0; index < cloneCount; index += 1) {
        fragment.appendChild(this.createClone(exactWidth));
      }

      this.track.appendChild(fragment);

      this.position = preservePosition && oldWidth > 0
        ? (oldRatio * this.cycleWidth) % this.cycleWidth
        : 0;

      this.track.style.left = `-${this.position.toFixed(2)}px`;
    }

    applyExactGroupWidth(group, width) {
      const value = `${width}px`;
      group.style.width = value;
      group.style.minWidth = value;
      group.style.flexBasis = value;
    }

    createClone(width) {
      const clone = this.source.cloneNode(true);
      clone.classList.remove('isbm__group--source');
      clone.classList.add('isbm__group--clone');
      clone.dataset.isbmClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      this.cleanClone(clone);
      this.applyExactGroupWidth(clone, width);
      return clone;
    }

    cleanClone(node) {
      const elements = [node, ...node.querySelectorAll('*')];

      elements.forEach((element) => {
        [...element.attributes].forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify-') || attribute.name === 'id') {
            element.removeAttribute(attribute.name);
          }
        });
      });
    }

    removeClones() {
      this.querySelectorAll('[data-isbm-clone="true"]').forEach((clone) => clone.remove());
    }

    start() {
      if (this.animationFrame || this.mode !== 'marquee' || !this.isVisible) return;
      this.lastTimestamp = 0;
      this.animationFrame = window.requestAnimationFrame(this.tick);
    }

    stop() {
      if (!this.animationFrame) return;
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = 0;
    }

    tick(timestamp) {
      if (!this.isConnected || this.mode !== 'marquee' || !this.isVisible) {
        this.stop();
        return;
      }

      if (!this.lastTimestamp) this.lastTimestamp = timestamp;

      const rawDelta = timestamp - this.lastTimestamp;
      const delta = rawDelta > 180 ? 16 : Math.min(Math.max(rawDelta, 0), 48);
      this.lastTimestamp = timestamp;

      if (!this.isPaused && this.cycleWidth > 0) {
        this.position += this.speed * (delta / 1000);

        if (this.position >= this.cycleWidth) {
          this.position %= this.cycleWidth;
        }

        this.track.style.left = `-${this.position.toFixed(2)}px`;
      }

      this.animationFrame = window.requestAnimationFrame(this.tick);
    }

    detectWindows() {
      const platform = navigator.userAgentData?.platform
        || navigator.platform
        || navigator.userAgent
        || '';

      return /windows|win32|win64/i.test(platform);
    }
  }

  customElements.define(ELEMENT_NAME, IronsideBenefitsMarquee);
})();
